import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { ITabs, MuiTabPanel, MuiTabs } from "../../../../components";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { childProgramNavigationSelector } from "../../../../redux/slices/project/projectSelector";
import { clearStartChildProgramNavigation } from "../../../../redux/slices/project/projectSlice";
import { PROJECT_TAB_INDEX } from "../../../createProject/components/createProjectTabs/CreateProjectTabs";
import { ChildProgramsMain } from "../../../createProject/components/createProjectTabs/tabs/childPrograms/ChildProgramsMain";
import { ProjectFeesAndExpenseMain } from "../../../createProject/components/createProjectTabs/tabs/feesAndExpense/ProjectFeesAndExpenseMain";
import { keyProjectDetailsSiteCoreKeys } from "../../../createProject/siteCore/SiteCoreKeys";
import { ProjectNavigationWrapper } from "./ProjectNavigation.style";
import ProjectTabView from "./ViewProjectTab/ViewProjectTab";
import ViewProjectFeeTab from "./viewProjectFeeTab/ViewProjectFeeTab";

interface ProjectNavigationProps {
  pageId: string;
  pageUrl: string;
  selectedTab: number;
  setSelectedTab: (tabIndex: number) => void;
  isNavigatedFromProgram?: boolean;
}

const ProjectNavigation: React.FC<ProjectNavigationProps> = ({
  pageId,
  pageUrl,
  selectedTab,
  setSelectedTab,
  isNavigatedFromProgram,
}) => {
  const { fetchLabel } = useSiteCore();
  const dispatch = useDispatch();
  const childProgramNavigation = useSelector(childProgramNavigationSelector);

  useEffect(() => {
    if (childProgramNavigation) {
      setSelectedTab(PROJECT_TAB_INDEX.CHILD_PROGRAM);
      dispatch(clearStartChildProgramNavigation());
    }
  }, [childProgramNavigation]);

  const { id: projectId } = useParams();

  const getLabel = (key: keyof typeof keyProjectDetailsSiteCoreKeys) => {
    return fetchLabel(
      keyProjectDetailsSiteCoreKeys.keyProjectDetails,
      keyProjectDetailsSiteCoreKeys[key],
    );
  };

  const tabIds = {
    projectTab: `${pageId}-tab1`,
    childProgramsTab: `${pageId}-tab2`,
    projectFeeTab: `${pageId}-tab3`,
    feeAndExpenseTab: `${pageId}-tab4`,
  };

  const tabs: ITabs[] = [
    {
      id: tabIds.projectTab,
      label: getLabel("projectTab"),
    },
    {
      id: tabIds.childProgramsTab,
      label: getLabel("childProgramsTab"),
    },
    {
      id: tabIds.projectFeeTab,
      label: getLabel("projectFeeTab"),
    },
    {
      id: tabIds.feeAndExpenseTab,
      label: getLabel("feeAndExpenseTab"),
    },
  ];

  const onTabChange = (_event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };
  return (
    <ProjectNavigationWrapper>
      <MuiTabs
        tabs={tabs}
        value={selectedTab}
        onChange={onTabChange}
        tabsTestIds={[
          `${pageId}-tab-button-1`,
          `${pageId}-tab-button-2`,
          `${pageId}-tab-button-3`,
          `${pageId}-tab-button-4`,
        ]}
        className="program-participant-tab"
      >
        <MuiTabPanel
          panelId={`${pageId}-tab1`}
          value={selectedTab}
          index={0}
          isPanelWithMount={true}
          data-testid={`${pageId}-tab-panel-1`}
          ariaLabelledById={tabIds.projectTab}
        >
          <ProjectTabView />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-tab2`}
          value={selectedTab}
          index={1}
          isPanelWithMount={true}
          data-testid={`${pageId}-tab-panel-2`}
          ariaLabelledById={tabIds.childProgramsTab}
        >
          <ChildProgramsMain
            isInViewMode={true}
            projectId={projectId}
            pageUrl={pageUrl}
            isNavigatedFromProgram={isNavigatedFromProgram}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-tab3`}
          value={selectedTab}
          index={2}
          isPanelWithMount={true}
          data-testid={`${pageId}-tab-panel-3`}
          ariaLabelledById={tabIds.projectFeeTab}
        >
          <ViewProjectFeeTab />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-tab4`}
          value={selectedTab}
          index={3}
          isPanelWithMount={true}
          data-testid={`${pageId}-tab-panel-4`}
          ariaLabelledById={tabIds.feeAndExpenseTab}
        >
          <ProjectFeesAndExpenseMain isViewOnly={true} isRefetch />
        </MuiTabPanel>
      </MuiTabs>
    </ProjectNavigationWrapper>
  );
};

export default ProjectNavigation;
